/**
 * ApprovalCard — det store kortet øverst i Godkjenn-køen (#155, redesign
 * steg 5a; opprinnelig Plan 02-10 / UI-SPEC §"4. Approval queue").
 *
 * Skissen `Stell_Godkjenn`:
 *   • Topp: InitialAvatar i barnets personfarge + navn + «levert for N timer
 *     siden», og til høyre en amber ventetid-chip («26 t») når oppgaven har
 *     ventet minst 6 timer. Navnet står alltid ved avataren, så fargen er aldri
 *     eneste kjennetegn.
 *   • Tittel (h2) med poengene «+10 p» til høyre.
 *   • Bevisbildet i full bredde når det finnes, med «Bilde slettes om N dager»
 *     (30 dagers lagring fra godkjenning; før godkjenning vises 30).
 *   • Avvis (sekundær) + Godkjenn (primær), 52 px. Avvis åpner årsakene inline
 *     (RejectReasonPanel) i stedet for et ark.
 *
 * SLA tiers (matches Plan 02-03 cron thresholds) står fortsatt i `data-tier`:
 *   - Tier 1: completed_at older than 6h, ≤24h
 *   - Tier 2: completed_at older than 24h
 *   - <6h → ingen chip
 * Begge er amber: amber betyr «venter» i den nye stilen (HANDOFF prinsipp 3).
 *
 * approving / rejecting drive button disable + spinner; both buttons
 * lock when either mutation is pending (T-02-08 mitigation: server is
 * still authoritative — UI lock is anti-double-click).
 */
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/primitives/Button';
import { Icon } from '../../components/primitives/Icon';
import { InitialAvatar } from '../../components/primitives/InitialAvatar';
import type { ColorSlot } from '../../components/primitives/PixelAvatar';
import { PhotoProofThumb } from '../../components/primitives/PhotoProofThumb';
import { RelativeTime } from '../../components/primitives/RelativeTime';
import type { PoolChoreInstance } from '../chores/useChoresQuery';
import { RejectReasonPanel } from './RejectReasonPanel';
import { FEATURED_CARD, WaitChip } from './queueParts';
import { waitingHours } from './useApprovalQueue';

const HOUR_MS = 3600_000;
const RETENTION_DAYS = 30;

export type ApprovalInstance = PoolChoreInstance & {
  completed_at: string | null;
  approved_at: string | null;
};

/** Callbackene kallstedet gir videre til mutasjonen. */
export interface MutateHandlers {
  onError: () => void;
}

interface ApprovalCardProps {
  instance: ApprovalInstance;
  claimer: { id: string; short_name: string; slot: ColorSlot };
  onApprove: (instanceId: string) => void;
  /** Avvisning med årsak; `onError` holder panelet åpent med feilen (#74). */
  onReject: (instanceId: string, reason: string, handlers: MutateHandlers) => void;
  approving?: boolean;
  rejecting?: boolean;
  /** Override for tests; defaults to live wall clock. */
  now?: Date;
}

type SlaTier = '1' | '2' | null;

function deriveSlaTier(completedAtIso: string | null, now: Date): SlaTier {
  const hours = waitingHours(completedAtIso, now) ?? 0;
  if (hours >= 24) return '2';
  if (hours >= 6) return '1';
  return null;
}

function deriveRetentionDays(approvedAtIso: string | null, now: Date): number {
  // Pre-approval: assume retention starts at completion; show 30d.
  if (!approvedAtIso) return RETENTION_DAYS;
  const elapsedDays = Math.floor(
    (now.getTime() - new Date(approvedAtIso).getTime()) / (24 * HOUR_MS),
  );
  return Math.max(0, RETENTION_DAYS - elapsedDays);
}

export function ApprovalCard({
  instance,
  claimer,
  onApprove,
  onReject,
  approving = false,
  rejecting = false,
  now,
}: ApprovalCardProps): ReactNode {
  const { t } = useTranslation();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectError, setRejectError] = useState<string | null>(null);
  const wallClock = now ?? new Date();
  const slaTier = deriveSlaTier(instance.completed_at, wallClock);
  const lock = approving || rejecting;
  const retentionDays = deriveRetentionDays(instance.approved_at, wallClock);
  const name = claimer.short_name || t('approvals.proposals.unknownKid');

  const chips = [
    t('approvals.reject.reasons.notDone'),
    t('approvals.reject.reasons.photoMissing'),
    t('approvals.reject.reasons.tryAgain'),
    t('approvals.reject.reasons.other'),
  ];

  return (
    <article
      data-testid="approval-card-root"
      data-approval-card={instance.id}
      aria-label={t('approvals.card.aria', { name, title: instance.title_snapshot })}
      className={FEATURED_CARD}
    >
      <header className="flex items-center gap-2.5">
        <InitialAvatar name={name} colorSlot={claimer.slot} size={36} decorative />
        <div className="min-w-0 flex-1">
          <p className="m-0 truncate text-body font-extrabold text-ink">{name}</p>
          {instance.completed_at ? (
            <p className="m-0 text-caption text-ink-2">
              {t('approvals.card.delivered')} <RelativeTime instant={instance.completed_at} />
            </p>
          ) : null}
        </div>
        {slaTier ? (
          <WaitChip
            data-testid="approval-card-sla"
            data-tier={slaTier}
            at={instance.completed_at}
            now={wallClock}
          />
        ) : null}
      </header>

      <div className="flex items-baseline justify-between gap-3">
        <h2 className="m-0 text-xl font-extrabold leading-tight text-ink">
          {instance.title_snapshot}
        </h2>
        <span className="shrink-0 text-base font-extrabold text-accent-text tnum">
          {t('approvals.card.points', { n: instance.point_value_snapshot })}
        </span>
      </div>

      {instance.proof_photo_url ? (
        <PhotoProofThumb
          url={instance.proof_photo_url}
          alt={t('approvals.proofAlt', { kid: name })}
          size="wide"
          retainsLeftDays={retentionDays}
        />
      ) : null}

      {rejectOpen ? (
        <RejectReasonPanel
          testIdPrefix="reject-reason"
          prompt={t('approvals.reject.prompt', { name })}
          chips={chips}
          submitLabel={t('approvals.reject.send')}
          submitting={rejecting}
          error={rejectError}
          onCancel={() => {
            setRejectOpen(false);
            setRejectError(null);
          }}
          onSubmit={(reason) => {
            setRejectError(null);
            onReject(instance.id, reason, {
              onError: () => setRejectError(t('approvals.error')),
            });
          }}
        />
      ) : (
        <footer className="flex gap-2.5">
          <Button
            variant="secondary"
            size={52}
            className="flex-1"
            data-testid="approval-card-reject"
            disabled={lock}
            onClick={() => {
              setRejectError(null);
              setRejectOpen(true);
            }}
          >
            {t('approvals.action.reject')}
          </Button>
          <Button
            variant="primary"
            size={52}
            className="flex-2"
            data-testid="approval-card-approve"
            disabled={lock}
            onClick={() => onApprove(instance.id)}
          >
            {approving ? (
              <span
                data-testid="approval-card-approve-spinner"
                aria-hidden
                className="inline-block h-3 w-3 animate-pulse rounded-full bg-on-primary"
              />
            ) : (
              <>
                <Icon.Check size={20} />
                {t('approvals.action.approve')}
              </>
            )}
          </Button>
        </footer>
      )}
    </article>
  );
}
